import { useEffect, useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { fetchJSON } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { fetchEmployees } from "@/lib/employees";

interface EmpRow {
  employee_id: string;
  employee_name: string;
  emp_business_amount: string;
  balance: string;
  collection: string;
  shortage: string;
  cum_shortage: string;
  note: string;
  denoms?: Record<string, string>;
}

const emptyRow = (): EmpRow => ({
  employee_id: "", employee_name: "",
  emp_business_amount: "0", balance: "0",
  collection: "", shortage: "0", cum_shortage: "0", note: "",
  denoms: {},
});

export default function EmployeeCashRecovery() {
  const { toast } = useToast();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [shift, setShift] = useState("S-1");
  const [rows, setRows] = useState<EmpRow[]>([emptyRow()]);
  const [saving, setSaving] = useState(false);

  // ── Filter/table state ─────────────────────────────────────────────────────
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterText, setFilterText] = useState("");
  const [showCount, setShowCount] = useState("All");
  const [editingId, setEditingId] = useState<string | null>(null);

  // ── Transaction modal ──────────────────────────────────────────────────────
  const [showTxn, setShowTxn] = useState(false);
  const [txnDate, setTxnDate] = useState(new Date().toISOString().split("T")[0]);
  const [txnShift, setTxnShift] = useState("S-1");
  const [txnRows, setTxnRows] = useState<any[]>([]);
  const [txnLoading, setTxnLoading] = useState(false);

  // ── Denominations modal ────────────────────────────────────────────────────
  const [showDenom, setShowDenom] = useState(false);
  const [denomRowIdx, setDenomRowIdx] = useState<number>(-1);
  const [denoms, setDenoms] = useState<Record<string, string>>({});

  // ── Denominations query ────────────────────────────────────────────────────
  const { data: denominationsData } = useQuery({
    queryKey: ["/api/master/denominations"],
    queryFn: async () => {
      try {
        const res = await fetchJSON("/api/master/denominations");
        const data = await res.json();
        return data.rows || [];
      } catch (err) {
        console.error("Failed to load denominations:", err);
        return [];
      }
    },
  });

  const activeDenominations = useMemo(() => {
    if (!denominationsData || denominationsData.length === 0) {
      return [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1];
    }
    return denominationsData
      .filter((d: any) => d.status && (d.status.toLowerCase() === "active" || d.status.toLowerCase() === "activated"))
      .map((d: any) => Number(d.denomination))
      .filter((n: number) => !isNaN(n))
      .sort((a: number, b: number) => b - a);
  }, [denominationsData]);

  // ── Data ───────────────────────────────────────────────────────────────────
  const { data: employeesData } = useQuery({
    queryKey: ["/api/employees"],
    queryFn: fetchEmployees,
  });
  const employees: any[] = employeesData || [];

  const { data: recoveryData, refetch: refetchRecovery } = useQuery({
    queryKey: ["/api/employee-cash-recovery"],
    queryFn: async () => {
      const res = await fetchJSON("/api/employee-cash-recovery");
      const data = await res.json();
      console.log("recoveryData frontend:", data.rows);
      return data.rows || [];
    },
  });
  const allRecoveries: any[] = recoveryData || [];

  // ── Computed ───────────────────────────────────────────────────────────────
  const totalRecoveryCash = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.collection) || 0), 0),
    [rows]
  );

  const filteredRecords = useMemo(() => {
    let r = [...allRecoveries];
    if (filterFrom) r = r.filter(x => (x.recovery_date || "").slice(0, 10) >= filterFrom);
    if (filterTo) r = r.filter(x => (x.recovery_date || "").slice(0, 10) <= filterTo);
    if (filterEmp && filterEmp !== "all") r = r.filter(x => String(x.employee_id) === filterEmp);
    if (filterText) {
      const t = filterText.toLowerCase();
      r = r.filter(x =>
        (x.employee_name || "").toLowerCase().includes(t) ||
        (x.notes || "").toLowerCase().includes(t)
      );
    }
    if (showCount !== "All") r = r.slice(0, Number(showCount));
    return r;
  }, [allRecoveries, filterFrom, filterTo, filterEmp, filterText, showCount]);

  // ── Auto-fill Daily Summary state ──────────────────────────────────────────
  const [dailySummary, setDailySummary] = useState<Record<string, any>>({});
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await fetchJSON(`/api/employee-cash-recovery/transactions?date=${date}&shift=${shift}`);
        const data = await res.json();
        if (active && data.success && data.rows) {
          const map: Record<string, any> = {};
          data.rows.forEach((r: any) => {
            map[String(r.employee_id)] = r;
          });
          setDailySummary(map);
        }
      } catch (err) {
        console.error("Failed to load daily summary:", err);
      } finally {
        if (active) setSummaryLoading(false);
      }
    };
    fetchSummary();
    return () => {
      active = false;
    };
  }, [date, shift]);

  // Update existing rows when dailySummary changes (e.g. date/shift changed)
  useEffect(() => {
    setRows(prev => {
      return prev.map(row => {
        if (!row.employee_id) return row;
        const summary = dailySummary[row.employee_id] || {};
        const businessAmt = Number(summary.cash_in) || 0;
        const col = Number(row.collection) || 0;
        return {
          ...row,
          emp_business_amount: String(businessAmt),
          balance: String(businessAmt),
          shortage: String((businessAmt - col).toFixed(3))
        };
      });
    });
  }, [dailySummary]);

  // ── Row helpers ────────────────────────────────────────────────────────────
  const updateRow = (idx: number, field: keyof EmpRow, value: string) => {
    setRows(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: value };
      if (field === "collection" || field === "emp_business_amount") {
        const biz = Number(field === "emp_business_amount" ? value : u[idx].emp_business_amount) || 0;
        const col = Number(field === "collection" ? value : u[idx].collection) || 0;
        u[idx].shortage = String((biz - col).toFixed(3));
      }
      return u;
    });
  };

  const selectEmployee = (idx: number, empId: string) => {
    const emp = employees.find((e: any) => String(e.id) === empId);
    const summary = dailySummary[empId] || {};
    const businessAmt = Number(summary.cash_in) || 0;
    setRows(prev => {
      const u = [...prev];
      u[idx] = { 
        ...u[idx], 
        employee_id: empId, 
        employee_name: emp?.employee_name || emp?.employeeName || "",
        emp_business_amount: String(businessAmt),
        balance: String(businessAmt),
        collection: "",
        shortage: String(businessAmt),
      };
      return u;
    });
  };

  const addRow = () => setRows(p => [...p, emptyRow()]);
  const removeRow = (idx: number) => setRows(p => p.filter((_, i) => i !== idx));

  // ── Denominations ──────────────────────────────────────────────────────────
  const denomTotal = useMemo(() => {
    return activeDenominations.reduce((s, val) => s + (Number(denoms[String(val)]) || 0) * val, 0);
  }, [activeDenominations, denoms]);

  const applyDenom = () => {
    setRows(prev => {
      const u = [...prev];
      const biz = Number(u[denomRowIdx].emp_business_amount) || 0;
      const col = denomTotal;
      u[denomRowIdx] = { 
        ...u[denomRowIdx], 
        collection: String(col),
        shortage: String((biz - col).toFixed(3)),
        denoms 
      };
      return u;
    });
    setShowDenom(false);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    const valid = rows.filter(r => r.employee_id && Number(r.collection) > 0);
    if (!valid.length) {
      toast({ variant: "destructive", title: "Select employee and enter collection amount" });
      return;
    }
    setSaving(true);
    try {
      for (const r of valid) {
        const payload = {
          recovery_date: date, shift,
          employee_id: r.employee_id,
          employee_name: r.employee_name,
          balance_amount: Number(r.balance) || 0,
          collection_amount: Number(r.collection) || 0,
          shortage_amount: Number(r.shortage) || 0,
          total_recovery_cash: totalRecoveryCash,
          notes: r.note || null,
        };
        const url = editingId ? `/api/employee-cash-recovery/${editingId}` : "/api/employee-cash-recovery";
        const method = editingId ? "PUT" : "POST";
        const res = await fetchJSON(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        const data = await res.json();
        if (!data.ok && !data.success) throw new Error(data.error || "Save failed");
      }
      toast({ title: "Success", description: "Saved successfully" });
      setRows([emptyRow()]);
      setEditingId(null);
      refetchRecovery();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row: any) => {
    setEditingId(String(row.id));
    setDate(row.recovery_date?.slice?.(0, 10) || new Date().toISOString().slice(0, 10));
    setShift(row.shift || "S-1");
    setRows([{
      employee_id: String(row.employee_id || ""),
      employee_name: row.employee_name || "",
      emp_business_amount: String(row.balance_amount || 0),
      balance: String(row.balance_amount || 0),
      collection: String(row.collection_amount || 0),
      shortage: String(row.shortage_amount || 0),
      cum_shortage: "0",
      note: row.notes || "",
      denoms: row.denoms || {},
    }]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this record?")) return;
    try {
      const res = await fetchJSON(`/api/employee-cash-recovery/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.ok || data.success) {
        toast({ title: "Deleted" });
        refetchRecovery();
      }
    } catch (err: any) {
      toast({ variant: "destructive", title: "Delete failed" });
    }
  };

  // ── Transactions modal ─────────────────────────────────────────────────────
  const loadTransactions = async (customDate?: any, customShift?: any) => {
    const d = (customDate && typeof customDate === "string") ? customDate : txnDate;
    const s = (customShift && typeof customShift === "string") ? customShift : txnShift;
    setTxnLoading(true);
    try {
      const res = await fetchJSON(`/api/employee-cash-recovery/transactions?date=${d}&shift=${s}`);
      const data = await res.json();
      if (data.success && data.rows) {
        setTxnRows(data.rows);
      } else {
        setTxnRows([]);
      }
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load transactions" });
      setTxnRows([]);
    } finally {
      setTxnLoading(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const fmt = (n: number) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cellInp = (extra?: React.CSSProperties): React.CSSProperties => ({
    height: 34, padding: "0 8px", border: "1px solid #d1d5db", borderRadius: 6,
    fontSize: 13, background: "#fff", color: "#111", width: "100%", boxSizing: "border-box", ...extra,
  });
  const th: React.CSSProperties = { padding: "9px 10px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", whiteSpace: "nowrap", background: "#f8fafc", borderBottom: "2px solid #e2e8f0" };
  const td: React.CSSProperties = { padding: "9px 10px", fontSize: 13, color: "#1e293b", borderBottom: "1px solid #f1f5f9" };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Announcement */}
      <div style={{ background: "#1e3a8a", padding: "10px 20px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ background: "#f59e0b", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4, letterSpacing: "0.05em", flexShrink: 0 }}>Announcement</span>
        <span style={{ color: "#cbd5e1", fontSize: 13 }}>
          Please complete employee cash recovery after all business activities are finished.{" "}
          <span style={{ color: "#f59e0b", fontWeight: 600 }}>This ensures correct cash-in-hand.</span>
        </span>
      </div>

      <div style={{ padding: "12px 20px 24px" }}>

        {/* See Transactions */}
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => { setTxnDate(date); setTxnShift(shift); setShowTxn(true); loadTransactions(date, shift); }}
            style={{ background: "#fff", border: "2px solid #2563eb", color: "#2563eb", padding: "6px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            📋 See Transactions
          </button>
        </div>

        {/* ── Blue Form Panel ─────────────────────────────────────────────── */}
        <div style={{ background: "linear-gradient(135deg,#1e40af 0%,#2563eb 60%,#3b82f6 100%)", borderRadius: 14, padding: "18px 20px 16px", marginBottom: 20, boxShadow: "0 8px 32px rgba(37,99,235,0.25)" }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ color: "#fff", fontSize: 17, fontWeight: 700, margin: 0 }}>Employee Cash Recovery</h2>
            <button style={{ background: "#f59e0b", color: "#fff", border: "none", borderRadius: 8, padding: "7px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Advance Collection
            </button>
          </div>

          {/* Date / Shift / Total */}
          <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ display: "flex", flexShrink: 0 }}>
              <span style={{ background: "#f59e0b", color: "#fff", padding: "0 12px", borderRadius: "6px 0 0 6px", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center" }}>Choose Date</span>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                style={{ height: 36, padding: "0 10px", border: "1px solid #d1d5db", borderRadius: "0 6px 6px 0", fontSize: 13, minWidth: 140 }} />
            </div>
            <div style={{ display: "flex", gap: 20, padding: "0 16px", background: "#fff", borderRadius: 8, height: 36, alignItems: "center", flexShrink: 0 }}>
              {["S-1", "S-2"].map(s => (
                <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#000", cursor: "pointer" }}>
                  <input type="radio" name="ecr-shift" checked={shift === s} onChange={() => setShift(s)} />{s}
                </label>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", fontWeight: 600, marginBottom: 2 }}>TOTAL RECOVERY CASH</div>
              <input readOnly value={fmt(totalRecoveryCash)}
                style={{ ...cellInp({ background: "#f0f9ff", fontWeight: 700, fontSize: 15, color: "#1e40af", width: "100%" }) }} />
            </div>
          </div>

          {/* Column labels */}
          <div style={{ display: "grid", gridTemplateColumns: "180px 150px 120px 1fr 110px 110px 140px 32px", gap: 6, marginBottom: 4, paddingRight: 2 }}>
            {["Employee Name", "Emp.Business Amt", "Balance", "Collection", "Shortage", "Cum.Shortage", "Note", ""].map((h, i) => (
              <span key={i} style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</span>
            ))}
          </div>

          {/* Employee rows */}
          {rows.map((row, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "180px 150px 120px 1fr 110px 110px 140px 32px", gap: 6, marginBottom: 6, alignItems: "center" }}>
              {/* Employee */}
              <select value={row.employee_id} onChange={e => selectEmployee(idx, e.target.value)} style={{ ...cellInp({ cursor: "pointer" }) }}>
                <option value="">choose Employee</option>
                {employees.map((e: any) => <option key={e.id} value={e.id}>{e.employee_name || e.employeeName}</option>)}
              </select>

              {/* Emp Business Amount */}
              <input type="number" placeholder="0.00" value={row.emp_business_amount}
                onChange={e => updateRow(idx, "emp_business_amount", e.target.value)} style={cellInp()} />

              {/* Balance */}
              <input type="number" placeholder="0.00" value={row.balance}
                onChange={e => updateRow(idx, "balance", e.target.value)} style={cellInp()} />

              {/* Collection + DENOM + ADV */}
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input type="number" placeholder="0.00" value={row.collection}
                  onChange={e => updateRow(idx, "collection", e.target.value)}
                  style={{ ...cellInp(), flex: 1, minWidth: 80 }} />
                <button onClick={() => {
                  setDenomRowIdx(idx);
                  setDenoms(rows[idx].denoms || {});
                  setShowDenom(true);
                }}
                  style={{ background: "#f59e0b", color: "#fff", border: "none", borderRadius: 5, padding: "5px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", height: 34, flexShrink: 0 }}>
                  DENOM
                </button>
                <button style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, padding: "5px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer", height: 34, flexShrink: 0 }}>
                  ADV
                </button>
              </div>

              {/* Shortage (auto) */}
              <input readOnly value={row.shortage} style={{ ...cellInp({ background: "#fef9c3", color: "#92400e", fontWeight: 600 }) }} />

              {/* Cum Shortage */}
              <input type="number" value={row.cum_shortage}
                onChange={e => updateRow(idx, "cum_shortage", e.target.value)} style={cellInp()} />

              {/* Note */}
              <input type="text" placeholder="Note" value={row.note}
                onChange={e => updateRow(idx, "note", e.target.value)} style={cellInp()} />

              {/* Add/Remove */}
              {idx === rows.length - 1 ? (
                <button onClick={addRow}
                  style={{ width: 30, height: 30, borderRadius: "50%", background: "#22c55e", color: "#fff", border: "none", fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  +
                </button>
              ) : (
                <button onClick={() => removeRow(idx)}
                  style={{ width: 30, height: 30, borderRadius: "50%", background: "#ef4444", color: "#fff", border: "none", fontSize: 18, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  ×
                </button>
              )}
            </div>
          ))}

          {/* CONFIRM */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 14, gap: 12 }}>
            <button onClick={handleConfirm} disabled={saving}
              style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, padding: "10px 44px", fontSize: 14, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", letterSpacing: "0.05em" }}>
              {saving ? "Saving..." : "✓ CONFIRM"}
            </button>
            {editingId && (
              <button onClick={() => { setRows([emptyRow()]); setEditingId(null); }}
                style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* ── Filter & Records Table ──────────────────────────────────────── */}
        <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          {/* Filter bar */}
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3 }}>SEARCH FROM</div>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3 }}>TO</div>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3 }}>EMPLOYEE</div>
              <select value={filterEmp} onChange={e => setFilterEmp(e.target.value)}
                style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", minWidth: 160 }}>
                <option value="all">All Employees</option>
                {employees.map((e: any) => <option key={e.id} value={e.id}>{e.employee_name || e.employeeName}</option>)}
              </select>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3 }}>SHOW</div>
              <select value={showCount} onChange={e => setShowCount(e.target.value)}
                style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff" }}>
                <option value="All">All</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3 }}>FILTER</div>
              <input placeholder="Type to filter..." value={filterText} onChange={e => setFilterText(e.target.value)}
                style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, width: 200 }} />
            </div>
            <button onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterEmp("all"); setFilterText(""); }}
              style={{ background: "#f59e0b", color: "#fff", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", alignSelf: "flex-end" }}>
              🔍 Search
            </button>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>S.No</th>
                  <th style={th}>Date</th>
                  <th style={th}>Employee</th>
                  <th style={th}>Shift</th>
                  <th style={{ ...th, textAlign: "right" }}>Busi. Cash</th>
                  <th style={{ ...th, textAlign: "right" }}>Balance</th>
                  <th style={{ ...th, textAlign: "right" }}>Collected</th>
                  <th style={{ ...th, textAlign: "right" }}>Shortage</th>
                  <th style={th}>Description</th>
                  <th style={{ ...th, textAlign: "center" }}>Action</th>
                  <th style={th}>User Log Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length === 0 ? (
                  <tr><td colSpan={11} style={{ ...td, textAlign: "center", color: "#94a3b8", padding: 32 }}>
                    No records found
                  </td></tr>
                ) : (
                  filteredRecords.map((r: any, i: number) => (
                    <tr key={String(r.id)} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{formatDate(r.recovery_date)}</td>
                      <td style={{ ...td, fontWeight: 500 }}>{r.employee_name || "—"}</td>
                      <td style={td}>
                        <span style={{ padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, background: r.shift === "S-1" ? "#dbeafe" : "#fef9c3", color: r.shift === "S-1" ? "#1d4ed8" : "#92400e" }}>
                          {r.shift || "S-1"}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>₹{fmt(r.balance_amount || 0)}</td>
                      <td style={{ ...td, textAlign: "right" }}>₹{fmt(r.balance_amount || 0)}</td>
                      <td style={{ ...td, textAlign: "right", color: "#16a34a", fontWeight: 600 }}>₹{fmt(r.collection_amount || 0)}</td>
                      <td style={{ ...td, textAlign: "right", color: r.shortage_amount > 0 ? "#dc2626" : "#374151" }}>
                        {fmt(r.shortage_amount || 0)}
                      </td>
                      <td style={{ ...td, color: "#64748b", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.notes || "—"}</td>
                      <td style={{ ...td, textAlign: "center" }}>
                        <button onClick={() => handleEdit(r)}
                          style={{ background: "#eff6ff", color: "#2563eb", border: "none", borderRadius: 5, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 4 }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(String(r.id))}
                          style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 5, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          Del
                        </button>
                      </td>
                      <td style={{ ...td, fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {r.created_by ? `Created: ${r.created_by} ` : ""}{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "10px 16px", fontSize: 12, color: "#94a3b8", borderTop: "1px solid #f1f5f9" }}>
            Showing {filteredRecords.length} of {allRecoveries.length} entries
          </div>
        </div>
      </div>

      {/* ── Denominations Modal ───────────────────────────────────────────── */}
      {showDenom && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, width: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            <div style={{ background: "#1e40af", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ color: "#fff", margin: 0, fontSize: 15, fontWeight: 700 }}>💰 Denomination Entry</h3>
              <button onClick={() => setShowDenom(false)} style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
            <div style={{ padding: 20, maxHeight: "360px", overflowY: "auto" }}>
              {activeDenominations.map((val) => {
                const key = String(val);
                const qtyStr = denoms[key] ?? "";
                const qty = Number(qtyStr) || 0;
                const totalVal = val * qty;
                return (
                  <div key={key} style={{ display: "grid", gridTemplateColumns: "60px 1fr 80px", gap: 10, marginBottom: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>₹{val}</span>
                    <input type="number" min="0" placeholder="0" value={qtyStr}
                      onChange={e => setDenoms(p => ({ ...p, [key]: e.target.value }))}
                      style={{ padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }} />
                    <span style={{ fontSize: 13, color: "#64748b", textAlign: "right" }}>= ₹{totalVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                );
              })}
              <div style={{ borderTop: "2px solid #e2e8f0", paddingTop: 12, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "#1e40af" }}>Total: ₹{denomTotal.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                <button onClick={applyDenom}
                  style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, padding: "9px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Transactions Modal ────────────────────────────────────────────── */}
      {showTxn && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "min(96vw,1050px)", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "#1e40af", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: 0 }}>Employees Transactions</h3>
              <button onClick={() => setShowTxn(false)} style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "none", borderRadius: 6, width: 28, height: 28, fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Transactions Date</span>
                <input type="date" value={txnDate} onChange={e => setTxnDate(e.target.value)}
                  style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} />
              </div>
              <div style={{ display: "flex", gap: 16, padding: "6px 16px", border: "1px solid #d1d5db", borderRadius: 8 }}>
                {["S-1", "S-2"].map(s => (
                  <label key={s} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, cursor: "pointer" }}>
                    <input type="radio" name="txn-shift" checked={txnShift === s} onChange={() => setTxnShift(s)} />{s}
                  </label>
                ))}
              </div>
              <button onClick={() => loadTransactions(txnDate, txnShift)}
                style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, padding: "7px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                View
              </button>
            </div>
            <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
              {txnLoading ? (
                <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
                  <thead>
                    <tr>
                      {["Salesman","MeterSale","LubSale","Petty cash","Cash in","Recovery","Swipe","Credit","Sale Discount","Expense","Emp. CashCollection","Shortage"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", textAlign: h === "Salesman" ? "left" : "right", fontWeight: 700, color: "#374151", background: "#f8fafc", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {txnRows.length === 0 ? (
                      <tr><td colSpan={12} style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>No data for selected date/shift. Select date, shift and click View.</td></tr>
                    ) : (
                      <>
                        {txnRows.map((r, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "9px 12px", fontWeight: 500 }}>{r.salesman}</td>
                            {(["meter_sale","lub_sale","petty_cash","cash_in","recovery","swipe","credit","sale_discount","expense","emp_cash_collection","shortage"] as const).map((k, j) => (
                              <td key={j} style={{ padding: "9px 12px", textAlign: "right" }}>{fmt(r[k] || 0)}</td>
                            ))}
                          </tr>
                        ))}
                        <tr style={{ background: "#d1fae5", fontWeight: 700 }}>
                          <td style={{ padding: "10px 12px", color: "#166534" }}>Total</td>
                          {(["meter_sale","lub_sale","petty_cash","cash_in","recovery","swipe","credit","sale_discount","expense","emp_cash_collection","shortage"] as const).map((k, j) => (
                            <td key={j} style={{ padding: "10px 12px", textAlign: "right", color: "#166534" }}>
                              {fmt(txnRows.reduce((s, r) => s + (Number(r[k]) || 0), 0))}
                            </td>
                          ))}
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowTxn(false)}
                style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, padding: "8px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


